// src/components/admin/sales/OverviewTab.tsx
// VISÃO GERAL DE VENDAS - MEGA INTEGRADA COM PERFORMANCE

import React, { useState } from 'react';
import {
    DollarSign,
    TrendingUp,
    Package,
    Clock,
    X,
    Download,
    BarChart3,
    TrendingDown,
    AlertTriangle,
    Award,
    Target,
    Activity
} from 'lucide-react';
import './OverviewTab.css';
import * as XLSX from 'xlsx';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import {
    useOverviewStats,
    useOverviewPerformance,
    useSalesChart,
    useYearChart,
} from '../../../hooks/useAdminQueries';

interface ProductStats {
    _id: string;
    title: string;
    type: string;
    salesCount: number;
    totalRevenueCents: number;
    averageTicketCents: number;
    percentageOfTotal: number;
    growthPercentage?: number;
}

interface TodaySale {
    time: string;
    username: string;
    email: string;
    product: string;
    plan: string;
    valueCents: number;
}


interface OverviewTabProps {
    onShowToast: (message: string, type: 'success' | 'error') => void;
}

const OverviewTab: React.FC<OverviewTabProps> = ({ onShowToast }) => {

    // ── 1. useState PRIMEIRO ──────────────────────────────
    const [showTodaySalesModal, setShowTodaySalesModal] = useState(false);
    const [todaySales, setTodaySales] = useState<TodaySale[]>([]);
    const [chartPeriod, setChartPeriod] = useState<'day' | 'week' | 'month'>('week');
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [selectedSemester, setSelectedSemester] = useState<'all' | '1' | '2'>('all');
    const [performancePeriod, setPerformancePeriod] = useState('30');
    const [showExportModal, setShowExportModal] = useState(false);
    const [exporting, setExporting] = useState(false);

    // ── 2. Hooks do React Query DEPOIS ───────────────────
    const { data: statsData, isLoading: loadingStats } = useOverviewStats();
    const { data: perfData, isLoading: loadingPerf } = useOverviewPerformance(performancePeriod);
    const { data: chartDataRes } = useSalesChart(chartPeriod);
    const { data: yearChartRes } = useYearChart(selectedYear, selectedSemester);

    // ── 3. Derivados ──────────────────────────────────────
    const stats = statsData ?? null;
    const performanceData = perfData ?? null;
    const chartData = chartDataRes?.chartData ?? [];
    const yearChartData = yearChartRes?.chartData ?? [];
    const loading = loadingStats || loadingPerf;

    const loadTodaySales = async () => {
        try {
            const token = localStorage.getItem('accessToken');

            const response = await fetch('/api/admin/sales/today-sales', {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.ok) {
                const data = await response.json();
                setTodaySales(data.sales || []);
                setShowTodaySalesModal(true);
            }
        } catch (error) {
            console.error('Erro:', error);
            onShowToast('Erro ao carregar vendas de hoje', 'error');
        }
    };

    const formatCurrency = (cents: number) => {
        return `R$ ${(cents / 100).toFixed(2)}`;
    };

    const formatTime = (dateStr: string) => {
        const date = new Date(dateStr);
        return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    };

    // ========== EXPORTAÇÃO ==========

    const handleExportExcel = async () => {
        try {
            setExporting(true);

            const wb = XLSX.utils.book_new();

            // ABA 1: Resumo
            const summaryData = [
                ['VISÃO GERAL DE VENDAS - GRAIA'],
                [''],
                ['ESTATÍSTICAS GERAIS'],
                ['Receita Total', formatCurrency(stats?.totalRevenueCents || 0)],
                ['Lucro Líquido', formatCurrency(stats?.netProfitCents || 0)],
                ['Total de Vendas', stats?.totalSales || 0],
                ['Vendas Hoje', stats?.newSalesToday || 0],
                ['Ticket Médio', formatCurrency(performanceData?.summary.averageTicketCents || 0)],
                ['Produtos Ativos', performanceData?.summary.totalProducts || 0],
            ];

            const ws1 = XLSX.utils.aoa_to_sheet(summaryData);
            XLSX.utils.book_append_sheet(wb, ws1, 'Resumo');

            // ABA 2: Top Produtos
            if (performanceData?.topProducts && performanceData.topProducts.length > 0) {
                const topProductsData = [
                    ['TOP 10 PRODUTOS MAIS VENDIDOS'],
                    [''],
                    ['Posição', 'Produto', 'Tipo', 'Vendas', 'Receita', 'Ticket Médio', '% do Total'],
                    ...performanceData.topProducts.map((p: ProductStats, idx: number) => [
                        idx + 1,
                        p.title,
                        p.type,
                        p.salesCount,
                        formatCurrency(p.totalRevenueCents),
                        formatCurrency(p.averageTicketCents),
                        p.percentageOfTotal.toFixed(1) + '%'
                    ])
                ];

                const ws2 = XLSX.utils.aoa_to_sheet(topProductsData);
                XLSX.utils.book_append_sheet(wb, ws2, 'Top Produtos');
            }

            // ABA 3: Produtos em Alta
            if (performanceData?.trending.rising && performanceData.trending.rising.length > 0) {
                const risingData = [
                    ['PRODUTOS EM ALTA'],
                    [''],
                    ['Produto', 'Vendas', 'Receita', 'Crescimento'],
                    ...performanceData.trending.rising.map((p: ProductStats) => [
                        p.title,
                        p.salesCount,
                        formatCurrency(p.totalRevenueCents),
                        `+${p.growthPercentage}%`
                    ])
                ];

                const ws3 = XLSX.utils.aoa_to_sheet(risingData);
                XLSX.utils.book_append_sheet(wb, ws3, 'Em Alta');
            }

            // ABA 4: Produtos em Queda
            if (performanceData?.trending.falling && performanceData.trending.falling.length > 0) {
                const fallingData = [
                    ['PRODUTOS EM QUEDA'],
                    [''],
                    ['Produto', 'Vendas', 'Receita', 'Queda'],
                    ...performanceData.trending.falling.map((p: ProductStats) => [
                        p.title,
                        p.salesCount,
                        formatCurrency(p.totalRevenueCents),
                        `${p.growthPercentage}%`
                    ])
                ];

                const ws4 = XLSX.utils.aoa_to_sheet(fallingData);
                XLSX.utils.book_append_sheet(wb, ws4, 'Em Queda');
            }

            // Salvar
            XLSX.writeFile(wb, `visao-geral-vendas-${Date.now()}.xlsx`);
            onShowToast('Excel exportado com sucesso!', 'success');

        } catch (error) {
            console.error('Erro ao exportar Excel:', error);
            onShowToast('Erro ao exportar Excel', 'error');
        } finally {
            setExporting(false);
            setShowExportModal(false);
        }
    };

    const handleExportPDF = async () => {
        try {
            setExporting(true);
            setShowExportModal(false);

            const element = document.getElementById('overview-content');
            if (!element) return;

            const canvas = await html2canvas(element, {
                scale: 2,
                useCORS: true,
                backgroundColor: '#FFFFFF'
            });

            const imgData = canvas.toDataURL('image/png');
            const pdf = new jsPDF('p', 'mm', 'a4');

            const pdfWidth = pdf.internal.pageSize.getWidth();
            const pdfHeight = pdf.internal.pageSize.getHeight();
            const imgWidth = pdfWidth;
            const imgHeight = (canvas.height * pdfWidth) / canvas.width;

            let heightLeft = imgHeight;
            let position = 0;

            pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
            heightLeft -= pdfHeight;

            while (heightLeft >= 0) {
                position = heightLeft - imgHeight;
                pdf.addPage();
                pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
                heightLeft -= pdfHeight;
            }

            pdf.save(`visao-geral-vendas-${Date.now()}.pdf`);
            onShowToast('PDF exportado com sucesso!', 'success');

        } catch (error) {
            console.error('Erro ao exportar PDF:', error);
            onShowToast('Erro ao exportar PDF', 'error');
        } finally {
            setExporting(false);
        }
    };

    if (loading) {
        return (
            <div className="ovw-loading">
                <div className="spinner"></div>
                <p>Carregando visão geral...</p>
            </div>
        );
    }

    return (
        <div className="ovw-container" id="overview-content">
            {/* HEADER */}
            <div className="ovw-header">
                <div className="ovw-header-left">
                    <h2>Visão Geral de Vendas</h2>
                    <div className="ovw-period-selector">
                        <span>Período:</span>
                        {['7', '30', '90', 'all'].map((p) => (
                            <button
                                key={p}
                                className={performancePeriod === p ? 'active' : ''}
                                onClick={() => setPerformancePeriod(p)}
                            >
                                {p === 'all' ? 'Tudo' : `${p}d`}
                            </button>
                        ))}
                    </div>
                </div>

                <button className="ovw-btn-export" onClick={() => setShowExportModal(true)}>
                    <Download size={18} />
                    Exportar Dados
                </button>
            </div>

            {/* CARDS DE ESTATÍSTICAS (6 cards: 3x3) */}
            <div className="ovw-stats-grid">
                {/* Receita Total */}
                <div className="ovw-stat-card">
                    <div className="ovw-stat-icon" style={{ background: '#003BD1' }}>
                        <DollarSign size={28} />
                    </div>
                    <div className="ovw-stat-info">
                        <span className="ovw-stat-label">Receita Total</span>
                        <span className="ovw-stat-value">
                            {formatCurrency(stats?.totalRevenueCents || 0)}
                        </span>
                        <span className="ovw-stat-description">Todas as vendas aprovadas</span>
                    </div>
                </div>

                {/* Lucro Líquido */}
                <div className="ovw-stat-card highlight">
                    <div className="ovw-stat-icon" style={{ background: '#10b981' }}>
                        <TrendingUp size={28} />
                    </div>
                    <div className="ovw-stat-info">
                        <span className="ovw-stat-label">Lucro Líquido</span>
                        <span className="ovw-stat-value">
                            {formatCurrency(stats?.netProfitCents || 0)}
                        </span>
                        <span className="ovw-stat-description">Receita - Comissões</span>
                    </div>
                </div>

                {/* Total de Vendas */}
                <div className="ovw-stat-card">
                    <div className="ovw-stat-icon" style={{ background: '#3b82f6' }}>
                        <Package size={28} />
                    </div>
                    <div className="ovw-stat-info">
                        <span className="ovw-stat-label">Total de Vendas</span>
                        <span className="ovw-stat-value">{stats?.totalSales || 0}</span>
                        <span className="ovw-stat-description">Transações concluídas</span>
                    </div>
                </div>

                {/* Vendas Hoje */}
                <div className="ovw-stat-card clickable" onClick={loadTodaySales}>
                    <div className="ovw-stat-icon" style={{ background: '#f59e0b' }}>
                        <Clock size={28} />
                    </div>
                    <div className="ovw-stat-info">
                        <span className="ovw-stat-label">Vendas Hoje</span>
                        <span className="ovw-stat-value">{stats?.newSalesToday || 0}</span>
                        <span className="ovw-stat-description">Clique para ver detalhes</span>
                    </div>
                </div>

                {/* Ticket Médio */}
                <div className="ovw-stat-card">
                    <div className="ovw-stat-icon" style={{ background: '#8b5cf6' }}>
                        <Target size={28} />
                    </div>
                    <div className="ovw-stat-info">
                        <span className="ovw-stat-label">Ticket Médio</span>
                        <span className="ovw-stat-value">
                            {formatCurrency(performanceData?.summary.averageTicketCents || 0)}
                        </span>
                        <span className="ovw-stat-description">Valor médio por venda</span>
                    </div>
                </div>

                {/* Produtos Ativos */}
                <div className="ovw-stat-card">
                    <div className="ovw-stat-icon" style={{ background: '#ec4899' }}>
                        <Activity size={28} />
                    </div>
                    <div className="ovw-stat-info">
                        <span className="ovw-stat-label">Produtos Ativos</span>
                        <span className="ovw-stat-value">{performanceData?.summary.totalProducts || 0}</span>
                        <span className="ovw-stat-description">Disponíveis no marketplace</span>
                    </div>
                </div>
            </div>

            {/* GRÁFICO DE VENDAS SEMANAL */}
            <div className="ovw-chart-section">
                <div className="ovw-chart-header">
                    <h3>
                        <BarChart3 size={20} />
                        Gráfico de Vendas
                    </h3>
                    <div className="ovw-chart-period-selector">
                        <button
                            className={chartPeriod === 'day' ? 'active' : ''}
                            onClick={() => setChartPeriod('day')}
                        >
                            24h
                        </button>
                        <button
                            className={chartPeriod === 'week' ? 'active' : ''}
                            onClick={() => setChartPeriod('week')}
                        >
                            7 dias
                        </button>
                        <button
                            className={chartPeriod === 'month' ? 'active' : ''}
                            onClick={() => setChartPeriod('month')}
                        >
                            30 dias
                        </button>
                    </div>
                </div>

                <div className="ovw-chart-container">
                    {chartData.length === 0 ? (
                        <div className="ovw-chart-empty">
                            <p>Nenhuma venda neste período</p>
                        </div>
                    ) : (
                        <div className="ovw-simple-chart">
                            {chartData.map((item: any, index: number) => {
                                const maxSales = Math.max(...chartData.map((d: any) => d.totalSales), 1);
                                const height = Math.max((item.totalSales / maxSales) * 100, 5);

                                return (
                                    <div key={index} className="ovw-chart-bar-wrapper">
                                        <div
                                            className="ovw-chart-bar"
                                            style={{ height: `${height}%` }}
                                            title={`${item.totalSales} vendas - ${formatCurrency(item.totalRevenue)}`}
                                        >
                                            <span className="ovw-chart-value">{item.totalSales}</span>
                                        </div>
                                        <span className="ovw-chart-label">
                                            {item._id.split(' ')[0].split('-').slice(1).join('/')}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>

            {/* NOVO: GRÁFICO DE LINHA ANUAL */}
            <div className="ovw-year-chart-section">
                <div className="ovw-chart-header">
                    <h3>
                        <Activity size={20} />
                        Evolução Anual de Vendas
                    </h3>
                    <div className="ovw-year-controls">
                        <select
                            value={selectedYear}
                            onChange={(e) => setSelectedYear(Number(e.target.value))}
                            className="ovw-year-select"
                        >
                            {[2026, 2027, 2028].map(year => (
                                <option key={year} value={year}>{year}</option>
                            ))}
                        </select>

                        <div className="ovw-semester-buttons">
                            <button
                                className={selectedSemester === 'all' ? 'active' : ''}
                                onClick={() => setSelectedSemester('all')}
                            >
                                Ano Todo
                            </button>
                            <button
                                className={selectedSemester === '1' ? 'active' : ''}
                                onClick={() => setSelectedSemester('1')}
                            >
                                1º Semestre
                            </button>
                            <button
                                className={selectedSemester === '2' ? 'active' : ''}
                                onClick={() => setSelectedSemester('2')}
                            >
                                2º Semestre
                            </button>
                        </div>
                    </div>
                </div>

                <div className="ovw-line-chart-container">
                    {yearChartData.length === 0 ? (
                        <div className="ovw-chart-empty">
                            <p>Nenhuma venda neste período</p>
                        </div>
                    ) : (
                        <svg className="ovw-line-chart" viewBox="0 0 800 300">
                            {/* Grid lines */}
                            {[0, 1, 2, 3, 4].map(i => (
                                <line
                                    key={i}
                                    x1="40"
                                    y1={50 + (i * 50)}
                                    x2="780"
                                    y2={50 + (i * 50)}
                                    stroke="var(--ovw-border)"
                                    strokeWidth="1"
                                    strokeDasharray="4"
                                />
                            ))}

                            {/* Line path */}
                            <polyline
                                fill="none"
                                stroke="#003BD1"
                                strokeWidth="3"
                                points={yearChartData.map((d: any, i: number) => {
                                    const maxSales = Math.max(...yearChartData.map((m: any) => m.totalSales));
                                    const x = 60 + (i * (700 / (yearChartData.length - 1 || 1)));
                                    const y = 250 - ((d.totalSales / (maxSales || 1)) * 180);
                                    return `${x},${y}`;
                                }).join(' ')}
                            />

                            {/* Data points */}
                            {yearChartData.map((d: any, i: number) => {
                                const maxSales = Math.max(...yearChartData.map((m: any) => m.totalSales));
                                const x = 60 + (i * (700 / (yearChartData.length - 1 || 1)));
                                const y = 250 - ((d.totalSales / (maxSales || 1)) * 180);

                                return (
                                    <g key={i}>
                                        <circle
                                            cx={x}
                                            cy={y}
                                            r="6"
                                            fill="#003BD1"
                                            stroke="white"
                                            strokeWidth="2"
                                        />
                                        <text
                                            x={x}
                                            y="280"
                                            textAnchor="middle"
                                            fontSize="12"
                                            fill="var(--ovw-text-secondary)"
                                        >
                                            {d.month}
                                        </text>
                                        <text
                                            x={x}
                                            y={y - 12}
                                            textAnchor="middle"
                                            fontSize="13"
                                            fontWeight="700"
                                            fill="var(--ovw-text-primary)"
                                        >
                                            {d.totalSales}
                                        </text>
                                    </g>
                                );
                            })}
                        </svg>
                    )}
                </div>
            </div>

            {/* TOP 10 PRODUTOS MAIS VENDIDOS */}
            {performanceData?.topProducts && performanceData.topProducts.length > 0 && (
                <section className="ovw-section">
                    <h3>
                        <Award size={20} />
                        Top 10 Produtos Mais Vendidos
                    </h3>
                    <div className="ovw-products-table">
                        <table>
                            <thead>
                                <tr>
                                    <th>Posição</th>
                                    <th>Produto</th>
                                    <th>Tipo</th>
                                    <th>Vendas</th>
                                    <th>Receita</th>
                                    <th>Ticket Médio</th>
                                    <th>% do Total</th>
                                </tr>
                            </thead>
                            <tbody>
                                {performanceData.topProducts.map((product: ProductStats, index: number) => (
                                    <tr key={product._id}>
                                        <td className="ovw-position">
                                            {index === 0 && <Award size={18} style={{ color: '#FFD700' }} />}
                                            {index === 1 && <Award size={18} style={{ color: '#C0C0C0' }} />}
                                            {index === 2 && <Award size={18} style={{ color: '#CD7F32' }} />}
                                            {index > 2 && `${index + 1}º`}
                                        </td>
                                        <td className="ovw-product-name">{product.title}</td>
                                        <td>
                                            <span className={`ovw-type-badge ovw-type-${product.type}`}>
                                                {product.type}
                                            </span>
                                        </td>
                                        <td>{product.salesCount}</td>
                                        <td className="ovw-revenue">{formatCurrency(product.totalRevenueCents)}</td>
                                        <td>{formatCurrency(product.averageTicketCents)}</td>
                                        <td>
                                            <span className="ovw-percentage">{product.percentageOfTotal.toFixed(1)}%</span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </section>
            )}

            {/* PRODUTOS EM ALTA E EM QUEDA */}
            <div className="ovw-trending-grid">
                {/* Em Alta */}
                <section className="ovw-section ovw-trending-up">
                    <h3>
                        <TrendingUp size={20} />
                        Produtos em Alta
                    </h3>
                    {!performanceData?.trending.rising || performanceData.trending.rising.length === 0 ? (
                        <p className="ovw-no-data">Nenhum produto em crescimento significativo</p>
                    ) : (
                        <div className="ovw-trending-list">
                            {performanceData.trending.rising.map((product: ProductStats) => (
                                <div key={product._id} className="ovw-trending-card ovw-up">
                                    <div className="ovw-trending-header">
                                        <span className="ovw-trending-title">{product.title}</span>
                                        <span className="ovw-trending-growth ovw-positive">
                                            <TrendingUp size={16} />
                                            +{product.growthPercentage}%
                                        </span>
                                    </div>
                                    <div className="ovw-trending-details">
                                        <span>{product.salesCount} vendas</span>
                                        <span>{formatCurrency(product.totalRevenueCents)}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </section>

                {/* Em Queda */}
                <section className="ovw-section ovw-trending-down">
                    <h3>
                        <TrendingDown size={20} />
                        Produtos em Queda
                    </h3>
                    {!performanceData?.trending.falling || performanceData.trending.falling.length === 0 ? (
                        <p className="ovw-no-data">Nenhum produto em queda significativa</p>
                    ) : (
                        <div className="ovw-trending-list">
                            {performanceData.trending.falling.map((product: ProductStats) => (
                                <div key={product._id} className="ovw-trending-card ovw-down">
                                    <div className="ovw-trending-header">
                                        <span className="ovw-trending-title">{product.title}</span>
                                        <span className="ovw-trending-growth ovw-negative">
                                            <TrendingDown size={16} />
                                            {product.growthPercentage}%
                                        </span>
                                    </div>
                                    <div className="ovw-trending-details">
                                        <span>{product.salesCount} vendas</span>
                                        <span>{formatCurrency(product.totalRevenueCents)}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </section>
            </div>

            {/* PRODUTOS COM BAIXA PERFORMANCE */}
            {performanceData?.worstProducts && performanceData.worstProducts.length > 0 && (
                <section className="ovw-section ovw-worst-products">
                    <h3>
                        <AlertTriangle size={20} />
                        Produtos com Baixa Performance
                    </h3>
                    <div className="ovw-products-table">
                        <table>
                            <thead>
                                <tr>
                                    <th>Produto</th>
                                    <th>Tipo</th>
                                    <th>Vendas</th>
                                    <th>Receita</th>
                                </tr>
                            </thead>
                            <tbody>
                                {performanceData.worstProducts.map((product: ProductStats) => (
                                    <tr key={product._id}>
                                        <td className="ovw-product-name">{product.title}</td>
                                        <td>
                                            <span className={`ovw-type-badge ovw-type-${product.type}`}>
                                                {product.type}
                                            </span>
                                        </td>
                                        <td>{product.salesCount}</td>
                                        <td>{formatCurrency(product.totalRevenueCents)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </section>
            )}

            {/* MODAL: Vendas de Hoje */}
            {showTodaySalesModal && (
                <div className="ovw-modal-overlay" onClick={() => setShowTodaySalesModal(false)}>
                    <div className="ovw-modal-content ovw-large" onClick={(e) => e.stopPropagation()}>
                        <div className="ovw-modal-header">
                            <h3>Vendas de Hoje</h3>
                            <button
                                className="ovw-btn-close-modal"
                                onClick={() => setShowTodaySalesModal(false)}
                            >
                                <X size={20} />
                            </button>
                        </div>
                        <div className="ovw-modal-body">
                            {todaySales.length === 0 ? (
                                <div className="ovw-empty-sales">
                                    <Package size={64} />
                                    <p>Nenhuma venda hoje ainda</p>
                                </div>
                            ) : (
                                <div className="ovw-today-sales-table">
                                    <table>
                                        <thead>
                                            <tr>
                                                <th>Horário</th>
                                                <th>Usuário</th>
                                                <th>Email</th>
                                                <th>Produto</th>
                                                <th>Valor</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {todaySales.map((sale, idx) => (
                                                <tr key={idx}>
                                                    <td className="ovw-time">{formatTime(sale.time)}</td>
                                                    <td>{sale.username}</td>
                                                    <td className="ovw-email">{sale.email}</td>
                                                    <td>{sale.product}</td>
                                                    <td className="ovw-value">
                                                        {formatCurrency(sale.valueCents)}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* MODAL: Exportação */}
            {showExportModal && (
                <div className="ovw-modal-overlay" onClick={() => !exporting && setShowExportModal(false)}>
                    <div className="ovw-modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="ovw-modal-header">
                            <h3>Exportar Dados</h3>
                            <button
                                className="ovw-btn-close-modal"
                                onClick={() => setShowExportModal(false)}
                                disabled={exporting}
                            >
                                <X size={20} />
                            </button>
                        </div>
                        <div className="ovw-modal-body">
                            <p className="ovw-export-description">
                                Escolha o formato de exportação dos dados da Visão Geral:
                            </p>

                            <div className="ovw-export-options">
                                <button
                                    className="ovw-export-option"
                                    onClick={handleExportExcel}
                                    disabled={exporting}
                                >
                                    <div className="ovw-export-icon" style={{ background: '#10b981' }}>
                                        <Download size={32} />
                                    </div>
                                    <div className="ovw-export-info">
                                        <strong>Excel (.xlsx)</strong>
                                        <p>Dados organizados em tabelas para análise</p>
                                    </div>
                                </button>

                                <button
                                    className="ovw-export-option"
                                    onClick={handleExportPDF}
                                    disabled={exporting}
                                >
                                    <div className="ovw-export-icon" style={{ background: '#ef4444' }}>
                                        <Download size={32} />
                                    </div>
                                    <div className="ovw-export-info">
                                        <strong>PDF (.pdf)</strong>
                                        <p>Relatório visual completo da tela</p>
                                    </div>
                                </button>
                            </div>

                            {exporting && (
                                <div className="ovw-exporting">
                                    <div className="spinner"></div>
                                    <p>Gerando arquivo...</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default OverviewTab;
